/**
 * Vest NQ-PERP volume in 2m bars, for the "Vest NQ vol" pane on MNQ/NQ charts.
 *
 * Contract (utt-engine, src/vest_scanner/volume.rs):
 * - REST GET {api}/vest-volume?symbol=MNQZ6.CME&from=ms&to=ms
 *     -> { vestSymbol, display, intervalMs, units, nqPerUnit, ready, historyFrom, tapeFrom, bars: VestBar[] }
 * - Socket "vest-volume:subscribe" {} joins the room; the engine answers with
 *   the live bars at once and then "vest-volume:update" { bars } (forming bar
 *   + the two before it) whenever they change.
 *
 * One series for both MNQ and NQ (both map to NDX-USD-PERP), so the store is
 * not keyed by symbol. Visibility lives in levelsStore (`vestVolume`).
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { DEFAULT_NQ_PER_UNIT, VestBar, mergeVestBars } from '../utils/vestVolume';

export const VEST_VOLUME_EVENT = 'vest-volume:update';
export const VEST_VOLUME_SUBSCRIBE = 'vest-volume:subscribe';
export const VEST_VOLUME_UNSUBSCRIBE = 'vest-volume:unsubscribe';
const NOT_READY_RETRY_MS = 15_000;

interface VestVolumeState {
  bars: Map<number, VestBar>;
  /** Bumped when REST history is merged (the pane redraws everything). */
  historyVersion: number;
  /** Bumped by live merges (the pane updates the tail only). */
  liveVersion: number;
  /** Earliest `from` already requested, ms. */
  loadedFrom: number | null;
  nqPerUnit: number;
  units: string;
  display: string;
  ready: boolean;
  tapeFrom: number | null;
  historyFrom: number | null;
  error: string | null;
  /** A REST history fetch is running (the rel-vol baseline history may still be missing). */
  historyLoading: boolean;

  applyLive: (payload: unknown) => void;
  fetchHistory: (symbol: string, fromMs: number) => Promise<void>;
  initialize: () => void;
  teardown: () => void;
}

let handlersRegistered = false;
/** Mounted panes (several charts can show the pane at once). */
let users = 0;
let retryTimer: ReturnType<typeof setInterval> | null = null;
let lastSymbol = '';
let inflight: Promise<void> | null = null;
/** One pending "history still loading" retry at a time. */
let notReadyTimer: ReturnType<typeof setTimeout> | null = null;

const onUpdate = (payload: unknown): void => useVestVolumeStore.getState().applyLive(payload);

// Rooms do not survive a reconnect: join again on every connect, and refetch
// the recent bars that may have closed while the socket was down.
const onConnect = (): void => {
  wsService.emit(VEST_VOLUME_SUBSCRIBE, {});
  const s = useVestVolumeStore.getState();
  if (lastSymbol && s.loadedFrom !== null) {
    let newest = 0;
    for (const t of s.bars.keys()) newest = Math.max(newest, t);
    void s.fetchHistory(lastSymbol, newest > 0 ? newest - 30 * 60_000 : s.loadedFrom);
  }
};

function tryRegister(): boolean {
  if (handlersRegistered) return true;
  if (!wsService.isConnected()) return false;
  wsService.on(VEST_VOLUME_EVENT, onUpdate);
  wsService.on('connect', onConnect);
  wsService.emit(VEST_VOLUME_SUBSCRIBE, {});
  handlersRegistered = true;
  return true;
}

export const useVestVolumeStore = create<VestVolumeState>((set, get) => ({
  bars: new Map(),
  historyVersion: 0,
  liveVersion: 0,
  loadedFrom: null,
  nqPerUnit: DEFAULT_NQ_PER_UNIT,
  units: 'NQ-PERP contracts',
  display: 'NQ-PERP',
  ready: false,
  tapeFrom: null,
  historyFrom: null,
  error: null,
  historyLoading: false,

  applyLive: (payload) => {
    const p = payload as { bars?: unknown } | null;
    if (!p || !Array.isArray(p.bars)) return;
    const next = mergeVestBars(get().bars, p.bars);
    if (next) set((s) => ({ bars: next, liveVersion: s.liveVersion + 1 }));
  },

  fetchHistory: async (symbol, fromMs) => {
    if (!symbol || !Number.isFinite(fromMs)) return;
    lastSymbol = symbol;
    const from = Math.floor(fromMs);
    // Already covered (a timeframe switch asks for the same span again).
    const loaded = get().loadedFrom;
    if (loaded !== null && from >= loaded && inflight === null && from < Date.now() - 60 * 60_000) return;
    const run = async () => {
      set({ historyLoading: true });
      try {
        const url = `${getApiBaseUrl()}/vest-volume?symbol=${encodeURIComponent(symbol)}&from=${from}`;
        const res = await fetch(url);
        const body = await res.json().catch(() => null);
        if (!res.ok) throw new Error(body?.error ?? `HTTP ${res.status}`);
        const bars: unknown[] = Array.isArray(body?.bars) ? body.bars : [];
        set((s) => {
          const next = mergeVestBars(s.bars, bars) ?? s.bars;
          return {
            bars: next,
            historyVersion: s.historyVersion + 1,
            loadedFrom: s.loadedFrom === null ? from : Math.min(s.loadedFrom, from),
            nqPerUnit: typeof body?.nqPerUnit === 'number' && body.nqPerUnit > 0 ? body.nqPerUnit : s.nqPerUnit,
            units: typeof body?.units === 'string' ? body.units : s.units,
            display: typeof body?.display === 'string' ? body.display : s.display,
            ready: body?.ready === true,
            tapeFrom: typeof body?.tapeFrom === 'number' ? body.tapeFrom : s.tapeFrom,
            historyFrom: typeof body?.historyFrom === 'number' ? body.historyFrom : s.historyFrom,
            error: null,
          };
        });
        // The writer was still loading its history: ask again shortly (one
        // retry pending at most, none once no pane/whales are shown).
        if (body?.ready === false && users > 0 && notReadyTimer === null) {
          notReadyTimer = setTimeout(() => {
            notReadyTimer = null;
            if (users > 0) void get().fetchHistory(symbol, from - 1);
          }, NOT_READY_RETRY_MS);
        }
      } catch (e) {
        set({ error: e instanceof Error ? e.message : String(e) });
      } finally {
        set({ historyLoading: false });
      }
    };
    const p = (inflight ?? Promise.resolve()).then(run);
    inflight = p;
    try {
      await p;
    } finally {
      if (inflight === p) inflight = null;
    }
  },

  initialize: () => {
    users += 1;
    startRegistering();
  },

  teardown: () => {
    users = Math.max(0, users - 1);
    if (users > 0) return;
    if (retryTimer !== null) clearInterval(retryTimer);
    retryTimer = null;
    if (notReadyTimer !== null) clearTimeout(notReadyTimer);
    notReadyTimer = null;
    if (!handlersRegistered) return;
    handlersRegistered = false;
    wsService.off(VEST_VOLUME_EVENT, onUpdate);
    wsService.off('connect', onConnect);
    // Leave the room so this tab stops receiving updates it no longer shows.
    wsService.emit(VEST_VOLUME_UNSUBSCRIBE, {});
  },
}));

/** Register now, or keep trying (every 2s) for as long as something shows Vest volume. */
function startRegistering(): void {
  if (tryRegister() || retryTimer !== null) return;
  retryTimer = setInterval(() => {
    if (users === 0 || tryRegister()) {
      if (retryTimer !== null) clearInterval(retryTimer);
      retryTimer = null;
    }
  }, 2000);
}

/**
 * WebSocketService.forceReconnect() replaces the socket object, dropping
 * every listener: forget ours before, register on the new socket after.
 */
export function resetVestVolumeSocketHandlers(): void {
  handlersRegistered = false;
}

export function initVestVolumeSocketHandlers(): void {
  if (users > 0) startRegistering();
}
